import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { Loader2 } from "lucide-react";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import { Button, cn } from "@eduviaos/ui-kit";
import { SectionCard } from "../../components/Section";
import { celebrateScore } from "../../lib/celebrateScore";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import { useSubmitPostReviewQuiz } from "../../lib/learning/hooks";
import type { PostReviewQuizResult, PostReviewQuizSession } from "../../lib/learning/types";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function PostReviewQuizRunner({
  session,
  onComplete,
}: {
  session: PostReviewQuizSession;
  onComplete: () => void;
}) {
  const reduce = useReducedMotion();
  const { strings, locale } = useLearnLocale();
  const submitMut = useSubmitPostReviewQuiz();
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, string | null>>({});
  const [result, setResult] = React.useState<PostReviewQuizResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const questions = session.questions;
  const total = questions.length;
  const question = questions[Math.min(index, total - 1)];
  const isLast = index >= total - 1;

  React.useEffect(() => {
    if (!result) return;
    celebrateScore(Math.round(result.score * 100), Boolean(reduce));
  }, [result, reduce]);

  if (result) {
    const scorePct = Math.round(result.score * 100);
    return (
      <SectionCard className="space-y-3 text-center">
        <p className="font-display text-lg font-semibold">{strings.followUpComplete}</p>
        <p className="text-sm font-semibold tabular-nums">
          {strings.yourScore}: {scorePct}%
        </p>
        {result.grade_reconciled && result.reconciliation_message && (
          <p className="bg-joy-amber/10 text-joy-amber rounded-lg px-3 py-2 text-left text-xs leading-relaxed">
            <span className="font-semibold">{strings.reconciliationNote}: </span>
            {result.reconciliation_message}
          </p>
        )}
        <p className="text-muted-foreground text-xs">
          {strings.postReviewHint} — {session.topic}
        </p>
        <Button className="w-full" onClick={onComplete}>
          {strings.continue}
        </Button>
      </SectionCard>
    );
  }

  if (!question) {
    return null;
  }

  const selected = answers[question.id] ?? null;
  const progress = Math.round(((index + 1) / total) * 100);

  async function submit() {
    setError(null);
    try {
      const payload = questions.map((q) => ({
        question_id: q.id,
        selected_option_id: answers[q.id] ?? null,
      }));
      const submitted = await submitMut.mutateAsync({ sessionId: session.id, answers: payload });
      setResult(submitted);
    } catch (err) {
      setError(apiErrorMessage(err, locale, strings, strings.runnerSubmitFailed));
    }
  }

  return (
    <div className="space-y-4">
      <SectionCard className="space-y-1 text-center">
        <p className="text-sm font-semibold">
          {strings.postReviewHint} — {session.topic}
        </p>
        <p className="text-muted-foreground text-xs">
          {total} {strings.questions.toLowerCase()}
        </p>
      </SectionCard>

      <SectionCard className="space-y-4">
        <div className="space-y-2">
          <div className="bg-muted h-1.5 overflow-hidden rounded-full">
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: reduce ? 0 : 0.22, ease: EASE_OUT }}
            />
          </div>
          <p className="text-muted-foreground text-xs tabular-nums">
            {strings.runnerQuestionOf(index + 1, total)}
          </p>
        </div>

        <p className="text-sm font-medium leading-relaxed">{question.prompt}</p>

        <div className="space-y-2">
          {question.options.map((opt) => {
            const active = selected === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: opt.id }))}
                className={cn(
                  "duration-120 w-full rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ease-out active:scale-[0.98]",
                  active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
                )}
              >
                <span className="text-muted-foreground mr-2 font-medium">{opt.id}.</span>
                {opt.label}
              </button>
            );
          })}
        </div>

        {error && <p className="text-destructive text-xs">{error}</p>}

        <div className="flex gap-2">
          {index > 0 && (
            <Button variant="outline" className="flex-1" onClick={() => setIndex((i) => i - 1)}>
              {strings.runnerPrevious}
            </Button>
          )}
          {!isLast ? (
            <Button className="flex-1" disabled={!selected} onClick={() => setIndex((i) => i + 1)}>
              {strings.runnerNext}
            </Button>
          ) : (
            <Button
              className="flex-1"
              disabled={!selected || submitMut.isPending}
              onClick={() => void submit()}
            >
              {submitMut.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {strings.runnerSubmitting}
                </>
              ) : (
                strings.runnerSubmitFollowUp
              )}
            </Button>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
